import { useContext, useEffect, useRef } from "react";

import useLogger from "@hd/logger/useLogger";

import { HubContext, HubContextCallbacks } from "./hub-context";
import { HubStatus } from "./hub-status";
import { useHubStore } from "./hub.store";
import { RealtimeUserInfoDto } from "@hd/types/dto/contact/realtime-user-info.dto";
import { RealtimeRequestLockedDto } from "@hd/types/dto/contact/realtime-request-locked.dto";
import { RealtimeRequestUnlockedDto } from "@hd/types/dto/contact/realtime-request-unlocked.dto";
import { RealtimeChatMessageDto } from "@hd/types/dto/realtime-message/realtime-chat-message.dto";

export const useHub = (
  companyId: number | null,
  callbacks: HubContextCallbacks,
  customUserId?: string,
) => {
  const context = useContext(HubContext);
  const hubStatus = useHubStore((state) => state.hubStatus);

  if (!context) {
    throw new Error("useHub must be used within a HubProvider");
  }

  const { subscribe, unsubscribe } = context;

  const { debug } = useLogger();

  const callbacksRef = useRef(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (companyId && companyId > 0) {
      const useHubCallbacks: HubContextCallbacks = {
        onMessages: (messages: RealtimeChatMessageDto[]) =>
          callbacksRef.current.onMessages?.(messages),
        onUsers: (users: RealtimeUserInfoDto[]) =>
          callbacksRef.current.onUsers?.(users),
        onQuotationLocked: (quotationData: RealtimeRequestLockedDto) =>
          callbacksRef.current.onQuotationLocked?.(quotationData),
        onQuotationUnlocked: (quotationData: RealtimeRequestUnlockedDto) =>
          callbacksRef.current.onQuotationUnlocked?.(quotationData),
        onRequestLocked: (quotationData: RealtimeRequestLockedDto) =>
          callbacksRef.current.onRequestLocked?.(quotationData),
        onRequestUnlocked: (quotationData: RealtimeRequestUnlockedDto) =>
          callbacksRef.current.onRequestUnlocked?.(quotationData),
      };
      subscribe(companyId, useHubCallbacks, customUserId).then(() => {
        debug("listening on connection", companyId);
      });

      return () => {
        unsubscribe(companyId, useHubCallbacks, customUserId).then(() => {
          debug("stop listening on connection", companyId);
        });
      };
    }
  }, [companyId, customUserId, subscribe, unsubscribe, debug]);

  return {
    hubStatus:
      companyId != null ? hubStatus[companyId] : HubStatus.Disconnected,
  };
};
